import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  BULK_SCRAP_REMARK_MAX_LENGTH,
  BULK_SCRAP_STATUSES,
  BulkScrapRequestDto,
  BulkScrapRequestService,
  BulkScrapStatus,
} from '../../services/bulk-scrap-request.service';
import { BULK_SCRAP_STATUS_META } from './bulk-scrap-display';

/** Inline status change form, shared by the table row, mobile card and details drawer. */
@Component({
  selector: 'app-bulk-scrap-status-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <form class="space-y-2 min-w-[180px]" (ngSubmit)="save()">
      <select
        name="status"
        [(ngModel)]="newStatus"
        aria-label="New status"
        class="block w-full min-h-[44px] text-sm border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 px-2">
        <option *ngFor="let opt of statuses" [ngValue]="opt">{{ statusMeta[opt].label }}</option>
      </select>
      <input
        type="text"
        name="remark"
        [(ngModel)]="remark"
        [maxlength]="remarkMaxLength"
        aria-label="Status remark (optional)"
        placeholder="Remark (optional)"
        class="block w-full min-h-[44px] text-sm border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 px-2" />
      <p *ngIf="errorMessage" role="alert" class="text-xs text-red-600">{{ errorMessage }}</p>
      <div class="flex gap-2">
        <button
          type="submit"
          [disabled]="isSaving"
          class="min-h-[44px] px-4 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
          {{ isSaving ? 'Saving…' : 'Save' }}
        </button>
        <button
          type="button"
          (click)="cancelled.emit()"
          [disabled]="isSaving"
          class="min-h-[44px] px-4 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200">
          Cancel
        </button>
      </div>
    </form>
  `,
})
export class BulkScrapStatusEditorComponent implements OnInit {
  private service = inject(BulkScrapRequestService);

  @Input({ required: true }) request!: BulkScrapRequestDto;
  @Output() saved = new EventEmitter<BulkScrapRequestDto>();
  @Output() cancelled = new EventEmitter<void>();

  readonly statuses = BULK_SCRAP_STATUSES;
  readonly statusMeta = BULK_SCRAP_STATUS_META;
  readonly remarkMaxLength = BULK_SCRAP_REMARK_MAX_LENGTH;

  newStatus!: BulkScrapStatus;
  remark = '';
  isSaving = false;
  errorMessage = '';

  ngOnInit(): void {
    this.newStatus = this.request.status;
  }

  save(): void {
    if (this.newStatus === this.request.status) {
      this.cancelled.emit();
      return;
    }
    this.isSaving = true;
    this.errorMessage = '';
    this.service
      .updateStatus(this.request.id, {
        status: this.newStatus,
        remark: this.remark.trim() || undefined,
      })
      .subscribe({
        next: (updated) => {
          this.isSaving = false;
          this.saved.emit(updated);
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage = err?.error?.message || 'Failed to update status.';
        },
      });
  }
}
